import { useEffect, useState } from 'react';
import { useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Column } from '@models/table.model.ts';
import { useTableDndColumnStore } from '@store/table-store/table-dnd-column.store.ts';

interface UseTableColumnDndParams {
  columns: Column[];
  tableKey?: string;
}

export function useTableColumnDnd({
  columns,
  tableKey = 'default'
}: UseTableColumnDndParams) {
  const dndStore = useTableDndColumnStore();

  const columnOrder =
    dndStore.getColumnOrder(tableKey) ?? columns.map((col) => col.id);
  const [orderedColumns, setOrderedColumns] = useState<Column[]>(columns);

  useEffect(() => {
    const ordered = columnOrder
      .map((id) => columns.find((col) => col.id === id))
      .filter((col): col is Column => Boolean(col));

    const hasChanged =
      ordered.length !== orderedColumns.length ||
      ordered.some(
        (col, i) =>
          col.id !== orderedColumns[i]?.id ||
          col.render !== orderedColumns[i]?.render
      );

    if (hasChanged) setOrderedColumns(ordered);
  }, [columns, columnOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columnOrder.indexOf(active.id);
    const newIndex = columnOrder.indexOf(over.id);

    const activeCol = columns.find((col) => col.id === active.id);
    const overCol = columns.find((col) => col.id === over.id);

    if (activeCol?.disabledDrag || overCol?.disabledDrag) return;

    const lastColumnId = columns[columns.length - 1]?.id;
    if (over.id === lastColumnId) return;

    const firstColumn = columns[0];
    if (
      firstColumn?.disabledDrag &&
      newIndex === 0 &&
      active.id !== firstColumn.id
    )
      return;

    const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
    dndStore.setColumnOrder(tableKey, newOrder);

    const reordered = newOrder
      .map((id) => columns.find((col) => col.id === id))
      .filter((col): col is Column => col !== undefined);

    setOrderedColumns(reordered);
  };

  return {
    orderedColumns,
    sensors,
    handleDragEnd
  };
}
